import React, { useState } from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    FormLabel,
    Stack,
    Switch,
    Typography,
} from "@mui/material";
import { Info } from "lucide-react";
import { ehEquipeBusifarm } from "../usersUtils";
import { mesmoPortal, portalDaConta, portalParaPayload, validarPortal, VISOES_DO_PORTAL } from "../portalUtils";
import { useEquipeBusifarm } from "../useEquipeBusifarm";

/**
 * Rascunho do portal de uma conta. Nasce do que está gravado e volta a ele quando a gravação muda
 * (a seção o remonta pela chave), então uma falha ao salvar não perde o que foi marcado.
 */
function EditorDoPortal({ user, salvo, onSalvar, salvando, bloqueado }) {
    const [rascunho, setRascunho] = useState(salvo);
    const erro = validarPortal(rascunho);
    const alterado = !mesmoPortal(rascunho, salvo);
    const ocupado = salvando || bloqueado;

    const alternarVisao = (visao) => (event) => {
        const marcada = event.target.checked;
        setRascunho((atual) => ({
            ...atual,
            visoes: marcada
                ? [...atual.visoes.filter((item) => item !== visao), visao]
                : atual.visoes.filter((item) => item !== visao),
        }));
    };

    return (
        <Stack spacing={2}>
            <FormControlLabel
                control={
                    <Switch
                        checked={rascunho.acesso}
                        onChange={(event) => setRascunho((atual) => ({ ...atual, acesso: event.target.checked }))}
                        disabled={ocupado}
                    />
                }
                label={
                    <Box>
                        <Typography variant="body2" fontWeight={600}>Acesso ao portal</Typography>
                        <Typography variant="caption" color="text.secondary">
                            A pessoa entra no portal do cliente e vê só as visões marcadas
                        </Typography>
                    </Box>
                }
            />

            {rascunho.acesso && (
                <FormControl component="fieldset" error={Boolean(erro)} disabled={ocupado}>
                    <FormLabel component="legend" sx={{ mb: 1, fontSize: 14, fontWeight: 600 }}>
                        Visões do portal
                    </FormLabel>
                    <FormGroup>
                        {VISOES_DO_PORTAL.map((visao) => (
                            <FormControlLabel
                                key={visao.value}
                                control={
                                    <Checkbox
                                        checked={rascunho.visoes.includes(visao.value)}
                                        onChange={alternarVisao(visao.value)}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="body2">{visao.label}</Typography>
                                        {visao.description && (
                                            <Typography variant="caption" color="text.secondary">
                                                {visao.description}
                                            </Typography>
                                        )}
                                    </Box>
                                }
                            />
                        ))}
                    </FormGroup>
                    {erro && <FormHelperText>{erro}</FormHelperText>}
                </FormControl>
            )}

            <Alert severity="info" icon={<Info size={16} />}>
                Ao salvar uma mudança aqui, quem estiver com o portal aberto sai em até 1 minuto: a pessoa precisa
                entrar de novo no portal.
            </Alert>

            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
            >
                <Typography variant="caption" color={alterado ? 'warning.main' : 'text.secondary'}>
                    {alterado
                        ? 'Mudança no portal ainda não salva. "Salvar alterações" não a leva.'
                        : 'O portal é salvo à parte, neste botão.'}
                </Typography>
                <Button
                    variant="outlined"
                    onClick={() => onSalvar?.(user, portalParaPayload(rascunho))}
                    disabled={!alterado || Boolean(erro) || ocupado}
                    sx={{ whiteSpace: 'nowrap' }}
                >
                    {salvando ? 'Salvando...' : 'Salvar acesso ao portal'}
                </Button>
            </Stack>
        </Stack>
    );
}

/**
 * Acesso ao portal do cliente e visões da conta (BBF-179, decisão D2 de 18/10/2026): só a equipe
 * Busifarm concede, e só ela vê esta seção. Grava pela rota própria do identity, com botão próprio:
 * o PUT do usuário ("Salvar alterações") não mexe no portal.
 *
 * @param user a conta como foi lida; nulo na criação
 * @param onSalvar `(conta, {acesso, visoes})`, chamado pelo botão da seção
 * @param bloqueado o resto do diálogo está salvando
 */
export default function PortalDoClienteSection({ user, onSalvar, salvando = false, bloqueado = false }) {
    const equipeBusifarm = useEquipeBusifarm();
    if (!equipeBusifarm) return null;

    const userId = user?.userId || user?.id;
    const salvo = portalDaConta(user);

    let conteudo;
    if (!userId) {
        conteudo = (
            <Typography variant="body2" color="text.secondary">
                Libere o portal depois de criar a conta, na edição do usuário.
            </Typography>
        );
    } else if (ehEquipeBusifarm(user?.specialAccessType)) {
        // O identity recusa o portal para ADMIN e SUPPORT (400): a equipe já entra sem ele.
        conteudo = (
            <Alert severity="info" icon={<Info size={16} />}>
                ADMIN e SUPPORT são a equipe Busifarm e já entram no portal sem este acesso.
            </Alert>
        );
    } else {
        conteudo = (
            <EditorDoPortal
                key={`${userId}:${salvo.acesso}:${salvo.visoes.join(',')}`}
                user={user}
                salvo={salvo}
                onSalvar={onSalvar}
                salvando={salvando}
                bloqueado={bloqueado}
            />
        );
    }

    return (
        <>
            <Divider />
            <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                    Portal do cliente
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Só a equipe Busifarm libera o portal e escolhe as visões de cada pessoa.
                </Typography>
                {conteudo}
            </Box>
        </>
    );
}
